/**
 * ============================================================================
 * FILE PREVIEW COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Preview component for uploaded files
 */

import React, { useState, useEffect } from 'react';
import { useStorage } from '../../hooks/useStorage';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import Modal from '../common/Modal';

/**
 * File Preview Component
 * @param {Object} props
 * @param {string} props.fileUrl - File URL
 * @param {string} props.fileName - File name
 * @param {string} props.fileType - File MIME type
 * @param {number} props.fileSize - File size in bytes
 * @param {boolean} props.showActions - Show action buttons
 * @param {Function} props.onDelete - Delete callback
 * @param {Function} props.onDownload - Download callback
 * @returns {JSX.Element}
 */
const FilePreview = ({
  fileUrl,
  fileName,
  fileType,
  fileSize,
  showActions = true,
  onDelete,
  onDownload,
}) => {
  const { generateImagePreview, generatePDFPreview, downloadFile, loading } = useStorage();
  const { showError } = useToast();
  const { language } = useI18n();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  useEffect(() => {
    const loadPreview = async () => {
      if (!fileUrl) return;

      try {
        setIsLoadingPreview(true);

        if (fileType?.startsWith('image/')) {
          const preview = await generateImagePreview(fileUrl, { width: 400, height: 400 });
          setPreviewUrl(preview.url || preview);
        } else if (fileType === 'application/pdf') {
          const preview = await generatePDFPreview(fileUrl, { page: 1, width: 400 });
          setPreviewUrl(preview.url || preview);
        } else {
          setPreviewUrl(null);
        }
      } catch (error) {
        console.error('Preview generation error:', error);
        setPreviewUrl(null);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    loadPreview();
  }, [fileUrl, fileType, generateImagePreview, generatePDFPreview]);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = () => {
    if (!fileType) return '📎';
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const handleDownload = async () => {
    if (onDownload) {
      onDownload();
    } else {
      try {
        await downloadFile(fileUrl, fileName);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du téléchargement' : 'Download failed')
        );
      }
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <>
      <Card variant="outlined" hover className="animate-fadeInUp">
        <div className="flex items-center gap-4">
          {/* Preview/Icon */}
          <div className="flex-shrink-0">
            {isLoadingPreview ? (
              <div className="w-16 h-16 flex items-center justify-center">
                <Spinner size="sm" />
              </div>
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt={fileName}
                className="w-16 h-16 object-cover rounded-xl cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setShowFullPreview(true)}
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-3xl">
                {getFileIcon()}
              </div>
            )}
          </div>

          {/* File Info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-black text-gray-900 dark:text-white truncate mb-1">
              {fileName || 'Unknown File'}
            </h4>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              {fileType && (
                <span className="capitalize">{fileType.split('/')[1] || fileType}</span>
              )}
              {fileSize && (
                <>
                  <span>•</span>
                  <span>{formatFileSize(fileSize)}</span>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {previewUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFullPreview(true)}
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  👁️
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                loading={loading}
                className="text-green-600 hover:text-green-700 dark:text-green-400"
              >
                💾
              </Button>
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-700 dark:text-red-400"
                >
                  🗑️
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Full Preview Modal */}
      <Modal
        isOpen={showFullPreview}
        onClose={() => setShowFullPreview(false)}
        title={
          <div className="flex items-center gap-2">
            <span className="text-xl">{getFileIcon()}</span>
            <span className="font-black text-gray-900 dark:text-white truncate">
              {fileName}
            </span>
          </div>
        }
      >
        <div className="space-y-4">
          {previewUrl ? (
            <div className="flex justify-center">
              <img
                src={previewUrl}
                alt={fileName}
                className="max-w-full max-h-96 object-contain rounded-xl"
              />
            </div>
          ) : (
            <div className="text-center p-12">
              <div className="text-6xl mb-4">{getFileIcon()}</div>
              <p className="text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Aperçu non disponible' : 'Preview not available'}
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={handleDownload} loading={loading}>
              {language === 'fr' ? 'Télécharger' : 'Download'} 💾
            </Button>
            <Button
              variant="gradient"
              fullWidth
              onClick={() => {
                window.open(fileUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              {language === 'fr' ? 'Ouvrir' : 'Open'} →
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FilePreview;
